import {computed, effect, inject, Injectable, linkedSignal, signal} from '@angular/core';
import {HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel,} from '@microsoft/signalr';
import {TokenService} from '../auth/token.service';
import {environment} from '../../environments/environment.dev';
import {ToastService} from './toast.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationSummary, NotificationType} from '../../api';
import {NotificationResource} from '../../api/resources';

@Injectable({providedIn: 'root'})
export class NotifService {
  readonly connectionState = signal<HubConnectionState>(HubConnectionState.Disconnected);
  private readonly tokenService = inject(TokenService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly apiNotificationService = inject(NotificationResource);
  private readonly notificationResource = this.apiNotificationService.getNotifications();
  notifications = linkedSignal(() => this.notificationResource.hasValue() ? this.notificationResource.value().notifications ?? [] : []);
  readonly unreadCount = computed(() => this.notifications()?.filter(n => !n.isRead).length);
  private connection: HubConnection | null = null;

  constructor() {
    effect(() => {
      const token = this.tokenService.token();
      if (token) {
        void this.startConnection(token);
      } else {
        void this.stopConnection();
      }
    });
  }

  private async startConnection(token: string): Promise<void> {
    await this.stopConnection();

    this.connection = new HubConnectionBuilder()
      .withUrl(`${environment.apiUrl}/hubs/notifications`, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on('ReceiveNotification', (notification: NotificationSummary) => {
      this.notifications.update(list => [{...notification, isRead: false}, ...list]);
      if (notification.type === NotificationType.TODOASSIGNEDCHANGED) {
        this.toastService.showMessage({
          severity: 'info',
          summary: this.translateService.instant('entity.todo'),
          detail: this.translateService.instant('notification.TODO_ASSIGNED_CHANGED', {'author': notification.authorFullName})
        })
      } else if (notification.type === NotificationType.TODOMENTIONED) {
        this.toastService.showMessage({
          severity: 'info',
          summary: this.translateService.instant('entity.todo'),
          detail: this.translateService.instant('notification.TODO_MENTIONNED', {'author': notification.authorFullName})
        })
      } else if (notification.type === NotificationType.REMINDTODO) {
        this.toastService.showMessage({
          severity: 'info',
          summary: this.translateService.instant('entity.todo'),
          detail: this.translateService.instant('notification.REMIND_TODO')
        })
      }

    });

    this.connection.onreconnecting(() =>
      this.connectionState.set(HubConnectionState.Reconnecting)
    );

    this.connection.onreconnected(() =>
      this.connectionState.set(HubConnectionState.Connected)
    );

    this.connection.onclose(() =>
      this.connectionState.set(HubConnectionState.Disconnected)
    );

    try {
      await this.connection.start();
      this.connectionState.set(HubConnectionState.Connected);
    } catch {
      this.connectionState.set(HubConnectionState.Disconnected);
    }
  }

  private async stopConnection(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.connectionState.set(HubConnectionState.Disconnected);
    }
  }
}
